import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Toast from '../../components/Toast';
import ThemeProvider from '../../theme/ThemeProvider';
import { MediaProvider } from './context/MediaContext';
import { buildFlow, STEP, STEP_LABEL } from './flow/useInterviewFlow';
import PermissionStep from './steps/PermissionStep';
import VerificationStep from './steps/verification/VerificationStep';
import InstructionsStep from './steps/InstructionsStep';
import MediaReadyGate from './steps/MediaReadyGate';
import VoiceInterviewHybridPage from '../ai-interview/VoiceInterviewHybridPage';
import AIInterviewHistory from '../ai-interview/AIInterviewHistory';
import AIInterviewCompletion from '../ai-interview/AIInterviewCompletion';
import { fetchInterviewConfig } from './config/interviewConfig';
import { getHistory, getResult, getTranscript, getStatus, getMockStatus } from '../ai-interview/utils/aiInterviewApi';
import { getMetadata } from '../../utils/encryptedStorage';

/**
 * Top-level proctored interview experience:
 *   access validation → [permission] → [verification] → [instruction] → interview → summary
 * Steps are generated from the backend config (nothing hardcoded). Camera/mic/screen are owned by
 * MediaProvider so they persist across every step into the interview.
 */
function getCompanyId() {
  try {
    const metadata = getMetadata();
    return String(metadata?.cid || metadata?.companyId || metadata?.company_id || '0');
  } catch { return '0'; }
}

// Synthetic flow config for the Job Seeker Mock Interview: a mic check only — no camera / screen /
// identity photo / recording / instructions. Voice + chat interview.
export const MOCK_FLOW_CONFIG = {
  steps: { permission: true, verification: false, instruction: false },
  verify: { photo: false, voice: false, network: false },
  record: { screen: false, camera: false, audio: false },
  interview: {
    camera_required: false, camera_strict: false,
    screen_required: false, screen_strict: false,
    mic_required: true,
  },
  theme_mode: 'light',
};

// ── ordered step runner (inside the providers) ──
const FlowRunner = ({ ids, config, rejoin, mock, onFinished }) => {
  // Onboarding steps (rejoin skips them); a media-readiness gate always runs right before the interview.
  const onboarding = useMemo(
    () => (rejoin ? [] : buildFlow(config).filter((k) => k !== STEP.INTERVIEW)),
    [config, rejoin],
  );
  const stepDefs = onboarding.map((k) => ({ key: k, label: STEP_LABEL[k] }));
  const [i, setI] = useState(0);
  const [ready, setReady] = useState(false);
  const [audioWarning, setAudioWarning] = useState(false);
  const completed = useMemo(() => {
    const c = {};
    for (let j = 0; j < i; j += 1) c[onboarding[j]] = true;
    return c;
  }, [onboarding, i]);

  const next = () => setI((v) => Math.min(v + 1, onboarding.length));

  if (i < onboarding.length) {
    const key = onboarding[i];
    if (key === STEP.PERMISSION) return <PermissionStep steps={stepDefs} completed={completed} config={config} onContinue={next} />;
    if (key === STEP.VERIFICATION) return <VerificationStep steps={stepDefs} completed={completed} config={config} ids={ids} onContinue={next} />;
    if (key === STEP.INSTRUCTION) return <InstructionsStep steps={stepDefs} completed={completed} config={config} onContinue={next} />;
  }

  // A completed read-aloud already checked the microphone; rejoin and non-voice flows retain the optional test.
  if (!ready) {
    return <MediaReadyGate requirements={config.interview} record={config.record} rejoin={rejoin}
      voiceVerified={onboarding.includes(STEP.VERIFICATION) && config.verify?.voice === true}
      ids={ids} onReady={(result) => {
      setAudioWarning(!!result?.audioWarning);
      setReady(true);
    }} />;
  }

  return (
    <VoiceInterviewHybridPage
      embedded
      onSessionComplete={onFinished}
      rejoin={rejoin}
      mock={mock}
      sessionId={ids.sessionId}
      candidateId={ids.candidateId}
      interviewCfg={config.interview}
      initialAudioWarning={audioWarning}
    />
  );
};

const InterviewExperience = ({ sessionId: sessionIdProp = '', candidateId: candidateIdProp = '', companyId: companyIdProp = '', onFinished }) => {
  const params = useParams();
  const [sessionId, setSessionId] = useState(sessionIdProp || params.sessionId || '');
  const [candidateId, setCandidateId] = useState(candidateIdProp || params.candidateId || '');
  // Job Seeker Mock Interview entry (test harness toggle): User ID + Job Listing ID.
  const [entryMode, setEntryMode] = useState('hr');       // 'hr' | 'mock'
  const [uid, setUid] = useState('');
  const [jobListingId, setJobListingId] = useState('');
  const [mockEntry, setMockEntry] = useState(null);       // {uid, jobListingId} once a mock interview begins
  const [config, setConfig] = useState(null);
  const [rejoin, setRejoin] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'error' });

  // "view previous interviews" browsing (access | history | result)
  const [mode, setMode] = useState('access');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [result, setResult] = useState(null);     // { transcript, meta, evaluation }
  const [resultId, setResultId] = useState(null);
  const [resultLoading, setResultLoading] = useState(false);

  const companyId = companyIdProp || getCompanyId();

  React.useEffect(() => {
    if (sessionIdProp) setSessionId(sessionIdProp);
    if (candidateIdProp) setCandidateId(candidateIdProp);
  }, [sessionIdProp, candidateIdProp]);

  React.useEffect(() => {
    if (!sessionIdProp || !candidateIdProp || config || busy) return;
    beginAccess();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionIdProp, candidateIdProp]);

  const requireIds = () => {
    if (!sessionId.trim() || !candidateId.trim()) {
      setToast({ message: 'Please enter both Session ID and Candidate ID.', type: 'error' });
      return false;
    }
    return true;
  };

  const beginAccess = async () => {
    if (!requireIds()) return;
    setBusy(true);
    setCompleted(false);
    // Rejoin if a live (unfinished) interview already exists for this candidate → skip onboarding.
    let isRejoin = false;
    try {
      const st = await getStatus(sessionId.trim(), candidateId.trim());
      if (st?.exists && st.finished) {
        setCompleted(true);
        setRejoin(false);
        setBusy(false);
        return;
      }
      isRejoin = !!(st && st.exists && !st.finished);
    } catch { /* no status / backend down → treat as a fresh interview */ }
    setRejoin(isRejoin);
    // Authoritative config from the backend (company → session), cached locally for offline.
    setConfig(await fetchInterviewConfig(companyId, sessionId.trim(), candidateId.trim()));
    setBusy(false);
  };

  // Job Seeker Mock Interview: start by (User ID + Job Listing ID). No backend proctoring config — the
  // synthetic MOCK_FLOW_CONFIG (mic check only) drives onboarding; the page uses the mock API.
  const beginMockAccess = async () => {
    if (!uid.trim() || !jobListingId.trim()) {
      setToast({ message: 'Please enter both User ID and Job Listing ID.', type: 'error' });
      return;
    }
    setBusy(true);
    setCompleted(false);
    let isRejoin = false;
    try {
      const st = await getMockStatus(uid.trim(), jobListingId.trim());
      // A finished run still enters the page; it detects "finished" on join and shows the feedback.
      isRejoin = !!(st && st.exists && !st.finished);
    } catch { /* no status / not started yet → fresh */ }
    setRejoin(isRejoin);
    setMockEntry({ uid: Number(uid.trim()), jobListingId: jobListingId.trim() });
    setConfig(MOCK_FLOW_CONFIG);
    setBusy(false);
  };

  const viewHistory = async () => {
    if (!requireIds()) return;
    setMode('history'); setHistoryLoading(true);
    try {
      const d = await getHistory(sessionId.trim(), candidateId.trim());
      setHistory(d.interviews || []);
    } catch (e) {
      setToast({ message: e.message || 'Could not load previous interviews.', type: 'error' });
    } finally { setHistoryLoading(false); }
  };

  const openResult = async (interviewId) => {
    setMode('result'); setResultLoading(true); setResult(null); setResultId(interviewId || null);
    try {
      const d = await getResult(sessionId.trim(), candidateId.trim(), interviewId, false);
      setResultId(d.interview_id || interviewId || null);
      setResult({ transcript: d.transcript_text || '', meta: d.metadata || {}, evaluation: d.evaluation || null });
    } catch {
      try {
        const t = await getTranscript(sessionId.trim(), candidateId.trim());
        setResult({ transcript: t.transcript_text || '', meta: t.metadata || {}, evaluation: null });
      } catch (e) {
        setToast({ message: e.message || 'Could not load this interview.', type: 'error' });
      }
    } finally { setResultLoading(false); }
  };

  const recalcResult = async (basis = 'current_configuration') => {
    setResultLoading(true);
    try {
      const d = await getResult(sessionId.trim(), candidateId.trim(), resultId || undefined, true, basis);
      setResult((r) => ({ ...(r || {}), evaluation: d.evaluation || null }));
    } catch (e) {
      setToast({ message: e.message || 'Could not recalculate.', type: 'error' });
    } finally { setResultLoading(false); }
  };

  const ids = mockEntry
    ? { companyId: '0', sessionId: String(mockEntry.jobListingId), candidateId: String(mockEntry.uid) }
    : { companyId, sessionId: sessionId.trim(), candidateId: candidateId.trim() };
  const inputCls = 'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 caret-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:border-white/15 dark:bg-slate-800/70 dark:text-white dark:placeholder-slate-400 dark:caret-violet-400 dark:focus:ring-violet-500/60';

  const toastEl = (
    <Toast message={toast.message} type={toast.type} visible={!!toast.message}
      onClose={() => setToast({ message: '', type: 'error' })} />
  );
  const pageWrap = 'min-h-[100dvh] w-full bg-slate-50 px-4 py-10 text-slate-900 dark:bg-[#0a0a12] dark:text-white';

  // Theme wraps the whole experience (incl. the access gate) and updates once config loads.
  return (
    <ThemeProvider mode={(config && config.theme_mode) || 'light'}>
      {completed ? (
        <div className={pageWrap}>
          <div className="mx-auto max-w-lg">
            <AIInterviewCompletion candidateView />
          </div>
          {toastEl}
        </div>
      ) : config ? (
        <MediaProvider ids={ids} record={config.record}>
          <FlowRunner ids={ids} config={config} rejoin={rejoin} mock={mockEntry} onFinished={onFinished} />
        </MediaProvider>
      ) : mode === 'history' ? (
        <div className={pageWrap}>
          <div className="mx-auto max-w-3xl">
            <AIInterviewHistory interviews={history} loading={historyLoading} onOpen={openResult} onBack={() => setMode('access')} />
          </div>
          {toastEl}
        </div>
      ) : mode === 'result' ? (
        <div className={pageWrap}>
          <div className="mx-auto max-w-3xl">
            <button type="button" onClick={() => setMode('history')}
              className="mb-4 text-sm font-medium text-primary-600 hover:underline dark:text-primary-300">← Back to interviews</button>
            {(!result && resultLoading) ? (
              <div className="text-slate-500 dark:text-slate-400">Loading interview…</div>
            ) : result ? (
              <AIInterviewCompletion
                transcript={result.transcript}
                meta={result.meta}
                evaluation={result.evaluation}
                evaluating={resultLoading}
                onRecalculate={recalcResult}
                onRestart={() => setMode('history')}
              />
            ) : (
              <div className="text-slate-500 dark:text-slate-400">No interview data.</div>
            )}
          </div>
          {toastEl}
        </div>
      ) : (
        <div className="flex min-h-[100dvh] w-full items-center justify-center bg-slate-50 px-4 text-slate-900 dark:bg-[#0a0a12] dark:text-white">
          <div className="w-full max-w-md">
            <div className="mb-6 text-center">
              <h1 className="bg-gradient-to-r from-primary-600 via-accent-500 to-secondary-500 bg-clip-text text-3xl font-extrabold text-transparent dark:from-primary-400 dark:via-accent-400 dark:to-secondary-400">
                WIZBOX Interview Suite
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Powered by Wiza · enter your interview access details to begin.</p>
            </div>
            <div className="space-y-3 rounded-3xl border border-slate-200 bg-white p-6 shadow-xl dark:border-white/10 dark:bg-slate-900/70 dark:shadow-none">
              {/* Test-only entry toggle: HR interview vs Job Seeker Mock interview. */}
              <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1 dark:bg-slate-800">
                {[['hr', 'HR Interview'], ['mock', 'Job Seeker Mock']].map(([m, label]) => (
                  <button key={m} type="button" onClick={() => setEntryMode(m)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                      entryMode === m
                        ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-white'
                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'}`}>
                    {label}
                  </button>
                ))}
              </div>
              {entryMode === 'hr' ? (
                <>
                  <label className="block text-sm">
                    <span className="mb-1 block text-slate-600 dark:text-slate-300">Session ID</span>
                    <input value={sessionId} onChange={(e) => setSessionId(e.target.value)} className={inputCls} placeholder="e.g. sess_abc" />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-slate-600 dark:text-slate-300">Candidate ID</span>
                    <input value={candidateId} onChange={(e) => setCandidateId(e.target.value)} className={inputCls} placeholder="e.g. 123" />
                  </label>
                  <button type="button" onClick={beginAccess} disabled={busy}
                    className="w-full rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:opacity-95 disabled:opacity-60">
                    {busy ? 'Checking…' : 'Begin'}
                  </button>
                  <button type="button" onClick={viewHistory}
                    className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5">
                    View previous interviews
                  </button>
                </>
              ) : (
                <>
                  <label className="block text-sm">
                    <span className="mb-1 block text-slate-600 dark:text-slate-300">User ID</span>
                    <input value={uid} onChange={(e) => setUid(e.target.value)} className={inputCls} placeholder="e.g. 42" />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-slate-600 dark:text-slate-300">Job Listing ID</span>
                    <input value={jobListingId} onChange={(e) => setJobListingId(e.target.value)} className={inputCls} placeholder="e.g. job-123" />
                  </label>
                  <p className="text-xs text-slate-400">Practice interview for a job you’ve applied to — voice + chat, with skill feedback at the end.</p>
                  <button type="button" onClick={beginMockAccess} disabled={busy}
                    className="w-full rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:opacity-95 disabled:opacity-60">
                    {busy ? 'Checking…' : 'Start mock interview'}
                  </button>
                </>
              )}
            </div>
          </div>
          {toastEl}
        </div>
      )}
    </ThemeProvider>
  );
};

export default InterviewExperience;
